import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  CloudUpload,
  Download,
  Description,
  Visibility,
  CheckCircle,
  Close,
  AttachMoney,
  LocalShipping,
  Receipt
} from '@mui/icons-material';
import { FaCloud, FaFileInvoice, FaFilePdf } from 'react-icons/fa';
import { MdFlag } from 'react-icons/md';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const CADInvoices = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [processId, setProcessId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const fileIndexRef = useRef(0);
  const processedFilesRef = useRef([]);

  const validateAndAddFiles = useCallback((selectedFiles) => {
    if (selectedFiles.length === 0) return;

    const pdfFiles = selectedFiles.filter(file =>
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length !== selectedFiles.length) {
      setError('Only PDF files are supported');
      return;
    }

    setFiles(prev => [...prev, ...pdfFiles]);
    setError('');
  }, []);

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    validateAndAddFiles(selectedFiles);
  };

  const removeFile = (index) => {
    setFiles(files => files.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndAddFiles(droppedFiles);
  };

  // Fetch status for current file processing
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const statusData = await apiCall(API_ENDPOINTS.CAD_INVOICES_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (statusData) {
        // Get values - handle both camelCase and PascalCase
        const progressPct = statusData.progressPercentage ?? statusData.ProgressPercentage ?? 0;
        const totalItems = statusData.totalItems ?? statusData.TotalItems ?? 0;
        const processedItems = statusData.processedItems ?? statusData.ProcessedItems ?? 0;
        const logs = statusData.logs ?? statusData.Logs ?? [];
        const currentStatus = statusData.status ?? statusData.Status ?? '';

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
          totalItems: totalItems,
          processedItems: processedItems,
        });

        // Update logs in global context
        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        // Check if processing is complete
        if (currentStatus === 'completed' || currentStatus === 'error') {
          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process
          completeActivityProcess(currentProcessId, currentStatus === 'completed');

          if (currentStatus === 'completed') {
            // Auto-download the result file
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              await downloadFile(currentProcessId);
            }

            // Track completed file
            processedFilesRef.current.push(files[fileIndexRef.current].name);

            // Check if there are more files to process
            if (fileIndexRef.current < files.length - 1) {
              // Process next file
              fileIndexRef.current++;
              setCurrentFileIndex(fileIndexRef.current);
              downloadTriggeredRef.current = false;
              await processNextFile();
            } else {
              // All files processed
              setProcessing(false);
              setSuccess(`Successfully processed ${processedFilesRef.current.length} of ${files.length} invoice(s)!`);
              setResults({
                processedCount: processedFilesRef.current.length,
                totalCount: files.length
              });
            }
          } else if (currentStatus === 'error') {
            setError('Processing failed. Check the activity log for details.');
            // Try next file if any
            if (fileIndexRef.current < files.length - 1) {
              fileIndexRef.current++;
              setCurrentFileIndex(fileIndexRef.current);
              downloadTriggeredRef.current = false;
              await processNextFile();
            } else {
              setProcessing(false);
              if (processedFilesRef.current.length > 0) {
                setSuccess(`Processed ${processedFilesRef.current.length} of ${files.length} invoice(s). Some files had errors.`);
              }
              setResults({
                processedCount: processedFilesRef.current.length,
                totalCount: files.length
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Process next file in queue
  const processNextFile = async () => {
    const file = files[fileIndexRef.current];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await apiCall(API_ENDPOINTS.CAD_INVOICES_PROCESS, {
        method: 'POST',
        body: formData
      });

      if (result.processId) {
        setProcessId(result.processId);
        processIdRef.current = result.processId;

        // Add to global activity log
        addActivityProcess(result.processId, `CAD Invoice: ${file.name}`, 'CAD Invoices');

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus();
        }, 1000);
        setIntervalRef(result.processId, intervalRef.current);
      } else {
        setError('Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error starting processing:', err);
      setError(`Failed to process ${file.name}: ${err.message}`);
      // Try next file if any
      if (fileIndexRef.current < files.length - 1) {
        fileIndexRef.current++;
        setCurrentFileIndex(fileIndexRef.current);
        await processNextFile();
      } else {
        setProcessing(false);
      }
    }
  };

  const processInvoices = async () => {
    if (files.length === 0) {
      setError('Please select PDF files before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);

    // Reset refs
    fileIndexRef.current = 0;
    setCurrentFileIndex(0);
    downloadTriggeredRef.current = false;
    processedFilesRef.current = [];

    // Start processing first file
    await processNextFile();
  };

  const downloadFile = async (downloadProcessId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.CAD_INVOICES_DOWNLOAD(downloadProcessId));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const fileName = files[fileIndexRef.current]?.name || 'CADInvoice.pdf';
      a.download = `Canadian-${fileName}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="CAD Invoices" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          CAD INVOICES
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Invoice Processing
              </Typography>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: files.length > 0 ? getCardBackground(theme) : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      PDF Invoice Files
                    </Typography>
                    {files.length > 0 && (
                      <Tooltip title={`${files.length} file(s) selected`}>
                        <Visibility sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Drag and Drop Zone */}
                  <Box
                    component="label"
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      display: 'block',
                      border: `2px dashed ${isDragging ? TOOL_COLORS.PDF.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.PDF.light
                        : files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                        bgcolor: files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.PDF.light,
                      }
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      accept=".pdf"
                      multiple
                      onChange={handleFileUpload}
                    />
                    {files.length === 0 ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {isDragging ? 'Drop files here' : 'Drag & drop PDF files here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF files only • Multiple files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {files.length} file(s) selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop more files to add
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Selected Files List */}
                  {files.length > 0 && (
                    <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                      {files.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(file.size)})
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => removeFile(index)}
                            disabled={processing}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}

                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={processInvoices}
                  disabled={files.length === 0 || processing}
                  startIcon={<FaCloud />}
                  sx={{
                    bgcolor: TOOL_COLORS.PDF.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Processing Invoices...' : 'Process Invoices'}
                </Button>

                {(files.length > 0 || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Information Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Info
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing invoices with AWS Textract...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.processedCount} of ${results.totalCount} Processed`}
                        secondary="Invoices converted to Canadian format"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaCloud color="#FF9500" />
                      </ListItemIcon>
                      <ListItemText
                        primary="AWS Textract Used"
                        secondary="Extracted tables and form data"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MdFlag color={TOOL_COLORS.PDF.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Canadian Format"
                        secondary="Standardized invoice layout"
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Download Info */}
              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Invoices have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Features */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Features:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                <Chip
                  icon={<FaCloud size={14} />}
                  label="AWS Textract"
                  size="small"
                  sx={{ bgcolor: '#FF9500', color: 'white' }}
                />
                <Chip
                  icon={<Receipt size={14} />}
                  label="Canadian Format"
                  size="small"
                  sx={{ bgcolor: TOOL_COLORS.PDF.primary, color: 'white' }}
                />
                <Chip
                  icon={<FaFilePdf size={14} />}
                  label="PDF Output"
                  size="small"
                  variant="outlined"
                />
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Select one or more PDF invoice files<br />
                2. Click "Process Invoices" to start<br />
                3. AWS Textract extracts invoice data<br />
                4. Invoices are converted to Canadian format<br />
                5. PDF files auto-download when complete<br /><br />
                <strong>Note:</strong> Processing time depends on file size and complexity.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CADInvoices;
